import {ChangeDetectionStrategy, Component, computed, effect, Injectable, input, output, signal, untracked} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors} from '@angular/forms';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from '@angular/cdk/drag-drop';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatButton} from '@angular/material/button';
import {MatError, MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {TranslateModule} from '@ngx-translate/core';
import {FeltValidatorer} from 'felles-ng2/infrastruktur/angular/feltValidatorer';
import {erHttpErrorResponse, erValideringsfeilException} from 'felles-ng2/infrastruktur/http/httpErrorResponseTyper';
import {feilkoderFraValideringsfeilException} from 'felles-ng2/modell/valideringsresultatModellHjelpere';
import {asynkArbeidPågårSignal} from 'felles-ng2/tjenester/AsynkArbeidPågårSignal';
import {i18n, I18nTekst, Oversettelser} from 'felles-ng2/tjenester/i18n/Oversettelser';
import {
    HtmlTekst,
    KorrelertSpørsmålModell,
    LagreSpørreskjemaSpesifikasjon,
    SpørreskjemaAdministrasjonModell,
    SpørreskjemaAdministrasjonRessursClient,
    SpørreskjemaAvtaleModell,
    SpørreskjemaId,
    SpørreskjemaValideringsfeil,
    Spørsmålstype
} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {standardFallbackFeilmelding, standardFeilmeldingerForErrorResponsMedFallback} from 'felles-ng2/tjenester/ressurser/feilhåndtering';
import {trimTilNull} from 'felles-ng2/støttefunksjoner/strenger';
import {InlineMeldingKomponent} from 'web-ng2/delt/komponenter/inline-melding/inline-melding';
import {KortHandlingerKomponent} from 'web-ng2/delt/komponenter/kort/kort-handlinger/kort-handlinger';
import {RiktekstRedigeringKomponent} from 'web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering';
import {SpørreskjemaSpørsmålRedigerKomponent, SpørsmålSkjema} from '../spørreskjema-spørsmål-rediger/spørreskjema-spørsmål-rediger';
import {VelgAvtalerKomponent} from '../velg-avtaler/velg-avtaler';
import {Alternativkilder} from './Alternativkilder';

/** 'alle': skjemaet er tilgjengelig for alle grupper, 'utvalgteAvtaler': kun for grupper tilknyttet valgte avtaler */
export type Tilgang = 'alle' | 'utvalgteAvtaler';

/**
 * Redigering av nytt eller eksisterende spørreskjema.
 *
 * Skjema som er i bruk av en eller flere undersøkelser er låst for strukturelle endringer: spørsmål kan ikke legges til,
 * fjernes eller flyttes, og type og svaralternativer kan ikke endres. Tekster og farger kan fortsatt endres.
 * Avtalene skjemaet er begrenset til kan endres også for skjema som er i bruk.
 *
 * Spørsmål kan benytte samme svaralternativer som et annet spørsmål, se {@link Alternativkilder}.
 *
 * Spørsmål som korrelerer med spørsmål i andre skjemaer (feks i en kopi av skjemaet) må ha samme type og svaralternativer som
 * disse. Endres type eller svaralternativer brytes korrelasjonen ved lagring, og bruker varsles om dette, se Korrelasjon.ts i spørreskjema-spørsmål-rediger.
 */
@Component({
    selector: 'spoerreskjema-rediger',
    templateUrl: 'spørreskjema-rediger.html',
    styleUrl: 'spørreskjema-rediger.scss',
    host: {'data-testid': 'spoerreskjema-rediger'},
    imports: [
        ReactiveFormsModule,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        MatButton,
        MatError,
        MatFormField,
        MatHint,
        MatIcon,
        MatInput,
        MatLabel,
        MatRadioButton,
        MatRadioGroup,
        TranslateModule,
        InlineMeldingKomponent,
        KortHandlingerKomponent,
        RiktekstRedigeringKomponent,
        SpørreskjemaSpørsmålRedigerKomponent,
        VelgAvtalerKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreskjemaRedigerKomponent {

    /** Skjema som skal redigeres, null for nytt skjema */
    spoerreskjema = input<SpørreskjemaAdministrasjonModell | null>(null);
    /** Kalles når bruker har lagret eller avbryter, returnerer lagret skjema ved lagring, null ved avbrutt */
    vedFerdig = output<SpørreskjemaAdministrasjonModell | null>();

    readonly erLaast = computed(() => (this.spoerreskjema()?.antallUndersoekelser ?? 0) > 0);
    readonly lagrer = asynkArbeidPågårSignal();
    readonly feilmelding = signal<string | null>(null);

    readonly skjema = new FormGroup({
        tittel: new FormControl<string>('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
        beskrivelse: new FormControl<HtmlTekst | null>(''),
        ekstraAnalyseprompt: new FormControl<string>('', {nonNullable: true, validators: FeltValidatorer.storStreng}),
        spoersmaal: new FormArray<SpørsmålSkjema>([], {validators: SpørreskjemaRedigerKomponent.strukturValidator}),
        tilgang: new FormControl<Tilgang>('alle', {nonNullable: true}),
        avtaler: new FormControl<SpørreskjemaAvtaleModell[]>([], {nonNullable: true})
    }, {validators: SpørreskjemaRedigerKomponent.avtalerPåkrevetValidator});

    /** Økes ved hver endring i spørsmålene, slik at avledet tilstand (alternativkandidater) beregnes på nytt */
    private readonly spørsmålsversjon = signal(0);

    readonly ingenKandidater: SpørsmålSkjema[] = [];
    readonly ingenKorrelerte: KorrelertSpørsmålModell[] = [];

    /** Spørsmål som kan velges som alternativkilde, per spørsmål */
    readonly alternativkandidater = computed(() => {
        this.spørsmålsversjon();
        const alle = this.skjema.controls.spoersmaal.controls;
        return new Map(alle.map(spørsmål => [spørsmål, Alternativkilder.kandidaterFor(spørsmål, alle)]));
    });

    /** Korrelerte spørsmål i andre skjemaer med annen type eller annet oppsett, per spørsmål. Korrelasjonen brytes for disse spørsmålene ved lagring */
    readonly avvikendeKorrelerte = computed(() => {
        this.spørsmålsversjon();
        const alle = this.skjema.controls.spoersmaal.controls;
        return new Map(alle.map(spørsmål => [spørsmål, SpørreskjemaSpørsmålRedigerKomponent.avvikendeKorrelerte(spørsmål)]));
    });

    /** Antall spørsmål der korrelasjonen med spørsmål i andre skjemaer brytes ved lagring */
    readonly antallBrutteKorrelasjoner = computed(() => [...this.avvikendeKorrelerte().values()].filter(avvikende => avvikende.length > 0).length);

    constructor(private avhengigheter: SpørreskjemaRedigerAvhengigheter, private oversettelser: Oversettelser) {
        this.skjema.controls.spoersmaal.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
            //spørsmål kan ha blitt slettet eller fått endret type eller kilde, slik at koblinger til alternativkilder må oppløses
            Alternativkilder.normaliser(this.skjema.controls.spoersmaal.controls);
            this.spørsmålsversjon.update(versjon => versjon + 1);
        });
        effect(() => {
            const spørreskjema = this.spoerreskjema();
            untracked(() => this.fyllSkjema(spørreskjema));
        });
    }

    leggTilGruppe(): void {
        this.skjema.controls.spoersmaal.push(SpørreskjemaSpørsmålRedigerKomponent.opprettSkjema({type: Spørsmålstype.grupperingstittel}));
    }

    leggTilSpoersmaal(): void {
        this.skjema.controls.spoersmaal.push(SpørreskjemaSpørsmålRedigerKomponent.opprettSkjema({type: Spørsmålstype.fritekst}));
    }

    slettSpoersmaal(indeks: number): void {
        this.skjema.controls.spoersmaal.removeAt(indeks);
    }

    spoersmaalFlyttet(event: CdkDragDrop<unknown>): void {
        const spørsmål = this.skjema.controls.spoersmaal;
        const rad = spørsmål.at(event.previousIndex);
        spørsmål.removeAt(event.previousIndex, {emitEvent: false});
        spørsmål.insert(event.currentIndex, rad);
    }

    async lagre(): Promise<void> {
        if (this.skjema.invalid) {
            this.skjema.markAllAsTouched();
            return;
        }

        this.feilmelding.set(null);
        try {
            const lagret = await this.lagrer.utfør(() => this.avhengigheter.lagre(this.spoerreskjema()?.id ?? null, this.somSpesifikasjon()));
            this.vedFerdig.emit(lagret);
        } catch (feil) {
            this.feilmelding.set(this.feilmeldingFor(feil).oversett(this.oversettelser));
        }
    }

    avbryt(): void {
        this.vedFerdig.emit(null);
    }

    /** Validerer at skjemaet har minst ett spørsmål, at første spørsmål er en gruppe og at alle grupper har minst ett spørsmål.
     * Tilsvarer valideringen i Spørreskjemadefinisjon.valider() i lederplan-tjenester. */
    static strukturValidator(kontroll: AbstractControl): ValidationErrors | null {
        const typer = (kontroll as FormArray<SpørsmålSkjema>).controls.map(rad => rad.controls.type.value);
        if (typer.length === 0) return {manglerSpoersmaal: true};
        if (typer[0] !== Spørsmålstype.grupperingstittel) return {foersteIkkeGruppe: true};
        const harGruppeUtenSpørsmål = typer.some((type, indeks) =>
          type === Spørsmålstype.grupperingstittel && (indeks + 1 === typer.length || typer[indeks + 1] === Spørsmålstype.grupperingstittel));
        return harGruppeUtenSpørsmål ? {gruppeUtenSpoersmaal: true} : null;
    }

    /** Ved tilgang 'utvalgteAvtaler' må minst én avtale være valgt */
    static avtalerPåkrevetValidator(kontroll: AbstractControl): ValidationErrors | null {
        const {tilgang, avtaler} = (kontroll as SpørreskjemaRedigerKomponent['skjema']).controls;
        return tilgang.value === 'utvalgteAvtaler' && avtaler.value.length === 0 ? {manglerAvtaler: true} : null;
    }

    // private hjelpemetoder

    private fyllSkjema(spørreskjema: SpørreskjemaAdministrasjonModell | null): void {
        const erLåst = this.erLaast();
        this.skjema.reset({
            tittel: spørreskjema?.tittel ?? '',
            beskrivelse: spørreskjema?.beskrivelse ?? '',
            ekstraAnalyseprompt: spørreskjema?.ekstraAnalyseprompt ?? '',
            tilgang: (spørreskjema?.avtaler.length ?? 0) > 0 ? 'utvalgteAvtaler' : 'alle',
            avtaler: spørreskjema?.avtaler ?? []
        });
        const spørsmål = this.skjema.controls.spoersmaal;
        spørsmål.clear({emitEvent: false});
        if (spørreskjema) {
            spørreskjema.spoersmaal.forEach(s => spørsmål.push(
              SpørreskjemaSpørsmålRedigerKomponent.opprettSkjema(s, erLåst, spørreskjema.korrelasjoner.find(korrelasjon => korrelasjon.spoersmaalId === s.id) ?? null),
              {emitEvent: false}));
            //koblingene til alternativkilder lagres ikke på serveren, og gjenskapes derfor ut fra like alternativer
            Alternativkilder.koblIdentiske(spørsmål.controls);
        } else {
            //et nytt skjema starter med en gruppe med ett spørsmål
            spørsmål.push(SpørreskjemaSpørsmålRedigerKomponent.opprettSkjema({type: Spørsmålstype.grupperingstittel}), {emitEvent: false});
            spørsmål.push(SpørreskjemaSpørsmålRedigerKomponent.opprettSkjema({type: Spørsmålstype.fritekst}), {emitEvent: false});
        }
        spørsmål.updateValueAndValidity();
    }

    private somSpesifikasjon(): LagreSpørreskjemaSpesifikasjon {
        const verdier = this.skjema.getRawValue();
        return {
            tittel: verdier.tittel.trim(),
            beskrivelse: verdier.beskrivelse ?? '',
            ekstraAnalyseprompt: trimTilNull(verdier.ekstraAnalyseprompt),
            spoersmaal: this.skjema.controls.spoersmaal.controls.map(SpørreskjemaSpørsmålRedigerKomponent.somSpesifikasjon),
            avtaleIder: verdier.tilgang === 'utvalgteAvtaler' ? verdier.avtaler.map(avtale => avtale.id) : []
        };
    }

    private feilmeldingFor(feil: unknown): I18nTekst {
        if (erValideringsfeilException<SpørreskjemaValideringsfeil>(feil)) {
            const feilkode = feilkoderFraValideringsfeilException<SpørreskjemaValideringsfeil>(feil)[0];
            return new I18nTekst(SpørreskjemaRedigerKomponent.valideringsfeiltekst(feilkode));
        }
        if (erHttpErrorResponse(feil)) return standardFeilmeldingerForErrorResponsMedFallback(feil);
        return standardFallbackFeilmelding(feil);
    }

    private static valideringsfeiltekst(feilkode: SpørreskjemaValideringsfeil): string {
        switch (feilkode) {
            case SpørreskjemaValideringsfeil.mangler_spørsmål: return i18n('Skjemaet må ha minst ett spørsmål');
            case SpørreskjemaValideringsfeil.første_spørsmål_ikke_grupperingstittel: return i18n('Første spørsmål i skjemaet må være en gruppe');
            case SpørreskjemaValideringsfeil.gruppe_uten_spørsmål: return i18n('Alle grupper må ha minst ett spørsmål');
            case SpørreskjemaValideringsfeil.ugyldige_alternativer: return i18n('Spørsmål av typen rangering, velg en av flere og velg noen av flere må ha minst ett svaralternativ, og alternativene må ha ulike verdier');
            case SpørreskjemaValideringsfeil.ugyldig_maksimalt_antall_valg: return i18n('Maksimalt antall avkrysninger må være mellom 1 og antall svaralternativer');
            case SpørreskjemaValideringsfeil.dupliserte_spørsmål: return i18n('Et spørsmål er angitt flere ganger');
            case SpørreskjemaValideringsfeil.korrelert_spørsmål_har_annen_type_eller_oppsett: return i18n('Et spørsmål har fått endret type eller svaralternativer, men sammenliknes fortsatt med tilsvarende spørsmål i et annet skjema. Last skjemaet på nytt og prøv igjen');
            case SpørreskjemaValideringsfeil.struktur_endret_for_skjema_i_bruk: return i18n('Skjemaet er i bruk, og kun tekster og farger kan endres');
            case SpørreskjemaValideringsfeil.skjema_i_bruk: return i18n('Skjemaet er i bruk og kan ikke slettes');
        }
    }
}

@Injectable()
export class SpørreskjemaRedigerAvhengigheter {
    constructor(private ressurs: SpørreskjemaAdministrasjonRessursClient) {}

    /** Oppretter nytt skjema dersom id ikke er angitt, ellers oppdateres eksisterende skjema */
    lagre(id: SpørreskjemaId | null, spesifikasjon: LagreSpørreskjemaSpesifikasjon): Promise<SpørreskjemaAdministrasjonModell> {
        return id === null
          ? this.ressurs.opprett(spesifikasjon).somPromise()
          : this.ressurs.oppdater(id, spesifikasjon).somPromise();
    }
}
